import {ELEMENT} from '../base/ElementTypes';

export interface HtmlQuestionReportAnswer {
    text: string;
    count: number;
    percentage: number;
    type: string;
}

export interface HtmlQuestionReportPayload {
    question: string;
    respondents: number;
    answers: HtmlQuestionReportAnswer[];
}

interface ReportQuestionSource {
    id?: string;
    type?: string;
    htmlSlotId?: string;
    question?: string;
    answers?: {text?: string; type?: string}[];
}

interface ReportSlideSource {
    name?: string;
    elements?: ReportQuestionSource[];
}

interface ReportSource {
    question?: string;
    userAnsweredCount?: number;
    answers?: {answerText?: string; noOfAnswers?: number; percentage?: number; type?: string}[];
}

const QUESTION_SLOT_TYPES = new Set<string>([ELEMENT.QUESTION, ELEMENT.MULTI_SELECT]);

export function findHtmlReportQuestion(
    slides: ReportSlideSource[],
    report: {questionElementId?: string; questionHtmlSlotId?: string; questionSlideName?: string},
    slideName: string
): ReportQuestionSource | undefined {
    const targetName = report.questionSlideName || slideName;
    if (report.questionHtmlSlotId) {
        const bySlot = slides
            .find((slide) => slide.name === targetName)
            ?.elements?.find((element) => element.htmlSlotId === report.questionHtmlSlotId && QUESTION_SLOT_TYPES.has(element.type ?? ''));
        if (bySlot) return bySlot;
    }

    if (!report.questionElementId) return undefined;

    return slides.flatMap((slide) => slide.elements ?? []).find((element) => element.id === report.questionElementId);
}

function percentageOf(count: number, respondents: number, given?: number): number {
    if (typeof given === 'number') return given;
    if (!respondents) return 0;
    return Math.round((count * 100) / respondents);
}

export function buildHtmlQuestionReportPayload(
    question: ReportQuestionSource | undefined,
    report: ReportSource | undefined
): HtmlQuestionReportPayload {
    const respondents = report?.userAnsweredCount ?? 0;
    const live = new Map((report?.answers ?? []).flatMap((answer) => (answer.answerText ? [[answer.answerText, answer] as const] : [])));
    const seen = new Set<string>();
    const answers: HtmlQuestionReportAnswer[] = [];

    (question?.answers ?? []).forEach((answer) => {
        const text = answer.text?.trim();
        if (!text || seen.has(text)) return;
        seen.add(text);
        const row = live.get(text);
        const count = row?.noOfAnswers ?? 0;
        answers.push({
            text,
            count,
            percentage: percentageOf(count, respondents, row?.percentage),
            type: answer.type || row?.type || '',
        });
    });

    live.forEach((row, text) => {
        if (seen.has(text)) return;
        const count = row.noOfAnswers ?? 0;
        answers.push({
            text,
            count,
            percentage: percentageOf(count, respondents, row.percentage),
            type: row.type || '',
        });
    });

    return {
        question: question?.question || report?.question || '',
        respondents,
        answers,
    };
}

function jsonForScript(value: unknown): string {
    return JSON.stringify(value).replace(/[<\u2028\u2029]/g, (char) => `\\u${char.charCodeAt(0).toString(16).padStart(4, '0')}`);
}

export function publishHtmlQuestionReport(slot: HTMLElement, payload: HtmlQuestionReportPayload): void {
    const doc = slot.ownerDocument;
    const view = doc.defaultView;
    if (!view) return;

    let script = slot.querySelector('script[data-incase-report-data]');
    if (!script) {
        script = doc.createElement('script');
        script.setAttribute('type', 'application/json');
        script.setAttribute('data-incase-report-data', '');
        slot.appendChild(script);
    }

    const json = jsonForScript(payload);
    if (script.textContent === json) return;
    script.textContent = json;
    slot.dispatchEvent(new view.CustomEvent('incase-report'));
}
